import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ReactionType } from '@/lib/api';
import { trackPostReaction } from '@/lib/analytics';

interface ReactionBarProps {
  postId: string;
  initialReaction?: ReactionType | null;
  surface: 'feed' | 'article';
  size?: 'sm' | 'md';
  invalidateKeys?: string[][];
  /** UI-082: Per-type reaction counts for social proof */
  reactionCounts?: Partial<Record<ReactionType, number>>;
}

const REACTIONS: Array<{ type: ReactionType; emoji: string; label: string; activeClass: string; hoverClass: string }> = [
  {
    type: 'fire',
    emoji: '🔥',
    label: 'Fire',
    activeClass: 'bg-orange-500/20 text-orange-400 ring-1 ring-orange-500/40',
    hoverClass: 'hover:bg-orange-500/10 hover:text-orange-400',
  },
  {
    type: 'like',
    emoji: '👍',
    label: 'Like',
    activeClass: 'bg-sky-500/20 text-sky-400 ring-1 ring-sky-500/40',
    hoverClass: 'hover:bg-sky-500/10 hover:text-sky-400',
  },
  {
    type: 'interesting',
    emoji: '🤔',
    label: 'Interesting',
    activeClass: 'bg-violet-500/20 text-violet-400 ring-1 ring-violet-500/40',
    hoverClass: 'hover:bg-violet-500/10 hover:text-violet-400',
  },
];

export function ReactionBar({ postId, initialReaction = null, surface, size = 'sm', invalidateKeys, reactionCounts }: ReactionBarProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ReactionType | null>(initialReaction);

  const reactMutation = useMutation({
    mutationFn: (type: ReactionType | null) => api.posts.react(postId, type),
    onSuccess: (data) => {
      setSelected(data.reaction);
      invalidateKeys?.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
    },
  });

  const handleClick = (type: ReactionType, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = selected === type ? null : type;
    setSelected(next); // optimistic
    reactMutation.mutate(next);
    trackPostReaction(postId, next, surface);
  };

  const padding = size === 'md' ? 'px-2.5 py-1.5' : 'px-2 py-1';
  const fontSize = size === 'md' ? 'text-base' : 'text-sm';

  return (
    <div className="flex items-center gap-1" role="group" aria-label="Post reactions">
      {REACTIONS.map(({ type, emoji, label, activeClass, hoverClass }) => {
        const isActive = selected === type;
        const count = reactionCounts?.[type] ?? 0;
        return (
          <button
            key={type}
            onClick={(e) => handleClick(type, e)}
            disabled={reactMutation.isPending}
            aria-label={isActive ? `Remove ${label} reaction` : `${label} reaction`}
            aria-pressed={isActive}
            title={label}
            className={`relative z-10 flex items-center gap-0.5 rounded-lg ${padding} ${fontSize} font-medium transition-all duration-200 active:scale-95 disabled:opacity-50 ${
              isActive ? activeClass : `text-text-tertiary ${hoverClass}`
            }`}
          >
            <span className={isActive ? 'scale-110' : ''}>{emoji}</span>
            {(count > 0 || isActive) && (
              <span className="tabular-nums text-[11px] leading-none">{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
